'use client';

import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBlockPart,
  useCommerceSource,
  tx,
  PortalLink,
  type AccountProfile,
} from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import {
  AccountField,
  buttonStyle,
  errorStyle,
  formStyle,
  leadStyle,
  linkRowStyle,
  mutedStyle,
  safeNext,
  titleStyle,
  useAccountErrorText,
  useQueryParam,
} from './accountShared';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * Sign in with an email and a password.
 *
 * <p>A wrong password, an unknown address and a locked account all read the
 * same — see `AccountErrorKind`. After signing in the page reloads into
 * `?next=` when that is a path on this site, else `successHref`: a full load,
 * not a client transition, so the header, the basket and every block re-read
 * who is signed in instead of each needing to be told.
 */
export interface SignInFormProps {
  title?: Localized;
  intro?: Localized;
  successHref?: string;
  registerHref?: string;
  forgotHref?: string;
}

export function SignInForm({
  title,
  intro,
  successHref = '/account',
  registerHref = '/register',
  forgotHref = '/forgot-password',
}: SignInFormProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const errorText = useAccountErrorText();
  const next = useQueryParam('next');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [current, setCurrent] = useState<AccountProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    commerce?.accountProfile?.()
      .then((profile) => {
        if (!cancelled) setCurrent(profile);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  if (!commerce?.signIn) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="SignInForm"
            adapterName="CommerceSource"
            reason={t('commerce.account.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!commerce?.signIn || busy) return;
    setBusy(true);
    setError(null);
    try {
      await commerce.signIn({ email: email.trim(), password });
      window.location.assign(safeNext(next.value) ?? successHref);
    } catch (failure) {
      setError(errorText(failure));
      setBusy(false);
    }
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 520 }}>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.account.signInTitle')}
        </h1>
        {current ? (
          <>
            <p style={leadStyle}>{t('commerce.account.alreadySignedIn', { email: current.email })}</p>
            <PortalLink href={successHref}>{t('commerce.account.goToAccount')}</PortalLink>
          </>
        ) : (
          <>
            <p {...part('intro')} style={leadStyle}>
              {tx(intro) || t('commerce.account.signInIntro')}
            </p>
            <form onSubmit={(event) => void submit(event)} style={formStyle}>
              {error && (
                <p role="alert" style={errorStyle}>
                  {error}
                </p>
              )}
              <AccountField
                id="sign-in-email"
                label={t('commerce.account.email')}
                value={email}
                onChange={setEmail}
                type="email"
                autoComplete="email"
                required
              />
              <AccountField
                id="sign-in-password"
                label={t('commerce.account.password')}
                value={password}
                onChange={setPassword}
                type="password"
                autoComplete="current-password"
                required
              />
              <button type="submit" disabled={busy} style={buttonStyle}>
                {busy ? t('commerce.account.signingIn') : t('commerce.account.signIn')}
              </button>
            </form>
            <div style={linkRowStyle}>
              <PortalLink href={forgotHref}>{t('commerce.account.forgotLink')}</PortalLink>
              <span style={mutedStyle}>
                {t('commerce.account.noAccount')}{' '}
                <PortalLink href={registerHref}>{t('commerce.account.registerLink')}</PortalLink>
              </span>
            </div>
          </>
        )}
      </div>
    </BlockRoot>
  );
}
